import {ApolloError} from 'apollo-client';
import gql from 'graphql-tag';
import {useMemo} from 'react';

import {
  ChartAggOption,
  SmoothingType,
  useWorkspaceTemplatesQuery,
  XAxisFormatValues,
} from '../../generated/graphql';
import {
  LinePlotSettingsV1,
  linePlotTemplateSettingsSchema,
} from '../../generated/workspaceTemplates';

/**
 * Convert a GraphQL response object to a Zod-compatible object.
 * - Strips __typename (GraphQL metadata)
 * - Removes null values (GraphQL uses null, Zod expects undefined for optional fields)
 */
function fromGraphqlToZod<T extends {__typename?: string}>(
  entity: T
): Omit<T, '__typename'> {
  const clone = {...entity};
  delete (clone as {__typename?: string}).__typename;
  for (const key of Object.keys(clone) as Array<keyof typeof clone>) {
    if (clone[key] === null) {
      delete clone[key];
    }
  }
  return clone as Omit<T, '__typename'>;
}

/**
 * This fetches the workspace templates the user created within the entity.
 * If the entity is an org that is NOT on the enterprise plan, then workspaceTemplates
 * will return an empty list.
 */
export const WORKSPACE_TEMPLATES_QUERY = gql`
  query WorkspaceTemplates($entityId: ID!, $userId: ID) {
    workspaceTemplates(entityId: $entityId, userId: $userId) {
      id
      createdAt
      createdBy {
        id
        name
        email
        username
      }
      updatedAt
      linePlotSettings {
        smoothingType
        smoothingWeight
        useRunsTableGroupingInPanels
        xAxis
        xLogScale
        xAxisMin
        xAxisMax
        maxRuns
        groupAgg
        xAxisFormat
        yLogScale
        yAxisMin
        yAxisMax
      }
    }
  }
`;

/**
 * Type-safe line plot settings generated from JSON schema.
 * We override the Zod enum types with GraphQL enum types to ensure type compatibility
 * across the UI. The Zod schema is still used for runtime validation.
 *
 * TODO: Remove this type override when GraphQL codegen is updated to emit string unions
 * instead of TypeScript enums. At that point, LinePlotSettingsV1 can be used directly.
 */
export type LinePlotTemplateSettings = Omit<
  LinePlotSettingsV1,
  'smoothingType' | 'xAxisFormat' | 'groupAgg'
> & {
  smoothingType?: SmoothingType;
  xAxisFormat?: XAxisFormatValues;
  groupAgg?: ChartAggOption;
};

// This type guarantees that fields exist, but value may be undefined
// This helps with typing when the query result hook is used
export type WorkspaceTemplate = {
  id: string;
  createdAt: string;
  createdBy: {
    id: string;
    name: string;
    email: string | undefined;
    username: string | undefined;
  };
  updatedAt: string | undefined;
  linePlotSettings: LinePlotTemplateSettings;
};

/**
 * This hook is used to fetch the workspace templates the user created in the selected account.
 *
 * @param entityId - this is the entityOrgId from the selected account.
 * The entities db table support different types - regular entity like team and org.
 * This allows us to simplify api and workspace_templates db cols.
 */
export const useWorkspaceTemplatesQueryResult = ({
  entityId,
  userId,
  skip,
}: {
  entityId: string | undefined;
  userId: string | undefined;
  skip?: boolean;
}): {
  workspaceTemplates: WorkspaceTemplate[];
  isLoadingWorkspaceTemplates: boolean;
  workspaceTemplatesError: ApolloError | undefined;
  refetchWorkspaceTemplates: () => void;
} => {
  const {data, loading, error, refetch} = useWorkspaceTemplatesQuery({
    variables: {entityId: entityId ?? '', userId},
    skip: entityId == null || userId == null || skip,
  });

  return useMemo(() => {
    if (loading || error != null) {
      return {
        workspaceTemplates: [],
        isLoadingWorkspaceTemplates: loading,
        workspaceTemplatesError: error,
        refetchWorkspaceTemplates: refetch,
      };
    }

    const workspaceTemplates: WorkspaceTemplate[] =
      data?.workspaceTemplates.map(template => {
        const linePlotSettingsData = fromGraphqlToZod(
          template.linePlotSettings ?? {}
        );

        // Validate and parse linePlotSettings using Zod schema
        const parseResult =
          linePlotTemplateSettingsSchema.safeParse(linePlotSettingsData);

        if (!parseResult.success) {
          console.error(
            'Failed to parse linePlotSettings for template',
            template.id,
            parseResult.error
          );
          // Return template with empty settings if validation fails
          return {
            id: template.id,
            createdAt: template.createdAt,
            createdBy: {
              id: template.createdBy.id,
              name: template.createdBy.name,
              email: template.createdBy.email ?? undefined,
              username: template.createdBy.username ?? undefined,
            },
            updatedAt: template.updatedAt ?? undefined,
            linePlotSettings: {} as LinePlotTemplateSettings,
          };
        }
        return {
          id: template.id,
          createdAt: template.createdAt,
          createdBy: {
            id: template.createdBy.id,
            name: template.createdBy.name,
            email: template.createdBy.email ?? undefined,
            username: template.createdBy.username ?? undefined,
          },
          updatedAt: template.updatedAt ?? undefined,
          // Cast Zod types to GraphQL enum types (values are identical, just different type systems)
          linePlotSettings: parseResult.data as LinePlotTemplateSettings,
        };
      }) ?? [];
    return {
      workspaceTemplates,
      isLoadingWorkspaceTemplates: loading,
      workspaceTemplatesError: error,
      refetchWorkspaceTemplates: refetch,
    };
  }, [data, loading, error, refetch]);
};

export const UPSERT_WORKSPACE_TEMPLATE_QUERY = gql`
  mutation UpsertWorkspaceTemplate(
    $entityId: ID!
    $id: ID
    $linePlotSettings: LinePlotSettingsInput
  ) {
    upsertWorkspaceTemplate(
      input: {entityId: $entityId, id: $id, linePlotSettings: $linePlotSettings}
    ) {
      id
    }
  }
`;

export const DELETE_WORKSPACE_TEMPLATE_QUERY = gql`
  mutation DeleteWorkspaceTemplate($id: ID!) {
    deleteWorkspaceTemplate(input: {id: $id}) {
      success
    }
  }
`;
